import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

export interface ServiceStatus {
  name: string
  status: 'checking' | 'connected' | 'failed' | 'timeout' | 'skipped'
  error?: string
  latencyMs?: number
  lastChecked: Date
}

interface ServiceHealthState {
  services: ServiceStatus[]
  allHealthy: boolean
  failedServices: ServiceStatus[]
  isChecking: boolean
  checkAll: (cognitoToken: string | null) => Promise<void>
  userIdentity: { email: string; name: string; geo: string } | null
  setUserIdentity: (identity: { email: string; name: string; geo: string } | null) => void
}

const ServiceHealthContext = createContext<ServiceHealthState | null>(null)

export function ServiceHealthProvider({ children }: { children: ReactNode }) {
  const [services, setServices] = useState<ServiceStatus[]>([])
  const [isChecking, setIsChecking] = useState(false)
  const [userIdentity, setUserIdentity] = useState<{ email: string; name: string; geo: string } | null>(null)

  const allHealthy = services.length > 0 && services.every(s => s.status === 'connected' || s.status === 'skipped')
  const failedServices = services.filter(s => s.status === 'failed' || s.status === 'timeout')

  const checkAll = useCallback(async (cognitoToken: string | null) => {
    setIsChecking(true)
    // Dynamic import to avoid circular deps
    const { checkAllServices } = await import('@/lib/health-checks')
    const results = await checkAllServices(cognitoToken)
    setServices(results)
    setIsChecking(false)
  }, [])

  return (
    <ServiceHealthContext.Provider value={{
      services, allHealthy, failedServices, isChecking,
      checkAll, userIdentity, setUserIdentity,
    }}>
      {children}
    </ServiceHealthContext.Provider>
  )
}

export function useServiceHealth() {
  const ctx = useContext(ServiceHealthContext)
  if (!ctx) throw new Error('useServiceHealth must be used within <ServiceHealthProvider>')
  return ctx
}
